import React from 'react';
import { FormControl,
  InputLabel,
  FormHelperText,
  makeStyles,
  Input,
  Theme } from '@material-ui/core';
import { FieldHookConfig, FieldValidator, useField } from 'formik';
import { fade } from '@material-ui/core/styles';

interface InputStylesProps {
  error: boolean,
  minWidth?: string
}

const useFormStyles = makeStyles(() => ({
  root: {
    marginTop: 15,
    '& input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      '-moz-appearance': 'none',
      margin: 0
    },
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
      '-webkit-appearance': 'textfield'
    }
  }
}));

const useInputStyles = makeStyles<Theme, InputStylesProps>((theme: Theme) => ({
  root: {
    'label + &': {
      marginTop: '20px'
    }
  },
  underline: {
    '&:before': {
      content: 'none'
    },
    '&:after': {
      content: 'none'
    }
  },
  input: {
    border: (props) => (props.error ? '1px solid #EA6172' : '1px solid #D0D6DA'),
    borderRadius: 4,
    minWidth: (props) => props.minWidth,
    fontSize: 16,
    lineHeight: '150%',
    fontWeight: 300,
    padding: '9px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 250, delay: 0 }),
    '&:focus': {
      boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0`,
      borderColor: (props) => (props.error ? '#EA6172' : theme.palette.primary.main)
    }
  }
}));

const useLabelStyles = makeStyles((theme: Theme) => ({
  root: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '150%',
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0)
  },
  formControl: {
    transform: 'none'
  },
  animated: {
    transition: 'none'
  }
}));


const useHelperTextStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(1)
  }
}));


interface TextInputProps {
  type: string,
  id: string,
  label: string,
  placeholder: string,
  fullWidth?: boolean,
  multiline?: boolean,
  rows?: number,
  minRows?: number,
  maxRows?: number,
  helperText?: string,
  minWidth?: string,
  disabled?: boolean,
  validate?: FieldValidator
  minValue?: number
  step?: number
  maxLength?: number
  autoFocus?: boolean
}

const TextInput: React.FC<TextInputProps> = (
  {
    fullWidth = true,
    multiline = false,
    minRows,
    maxRows,
    rows = minRows || 1,
    id,
    label,
    placeholder,
    type = 'text',
    minWidth,
    disabled = false,
    validate = undefined,
    minValue,
    step = 1,
    maxLength,
    autoFocus = false
  }
) => {
  const hookConfig:FieldHookConfig<string> = { name: id, validate, type };
  const [field, meta] = useField(hookConfig);

  const { onBlur, onChange, value } = field;
  const { error, touched } = meta;

  const formStyles = useFormStyles();
  const inputStyles = useInputStyles({ error: !!error && touched, minWidth });
  const labelStyles = useLabelStyles();
  const helperTextStyles = useHelperTextStyles();

  return (
    <FormControl
      fullWidth={fullWidth}
      classes={formStyles}
      error={!!error && touched}
      disabled={disabled}
    >
      <InputLabel htmlFor={id} classes={labelStyles} shrink>
        {label}
      </InputLabel>
      <Input
        classes={inputStyles}
        multiline={multiline}
        type={type}
        placeholder={placeholder}
        id={id}
        rows={rows}
        rowsMin={minRows}
        rowsMax={maxRows}
        name={id}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
        inputProps={{ min: minValue, step, maxLength }}
        autoFocus={autoFocus}
      />
      {touched && error ? <FormHelperText classes={helperTextStyles}>{error}</FormHelperText> : null}
    </FormControl>
  );
};

export default TextInput;

